import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  output
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { interval, map, startWith } from 'rxjs';
import { BasePageComponent } from '../../../../core/layout/components/abstracts/base-page.component';
import { LanguageStore } from '../../../../core/stores/LanguageStore';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { OtsStaffDashboardPreferencesService } from '../../core/services/OtsStaffDashboardPreferencesService';
import { OtsStaffOrdersService } from '../../core/services/OtsStaffOrdersService';
import { OtsStaffOrdersStore } from '../../core/stores/OtsStaffOrdersStore';
import { OtsStaffOrderCardUtils } from '../../core/utils/OtsStaffOrderCardUtils';
import { IOtsStaffOrderCard } from '../../core/interfaces/IOtsStaffOrderCard';
import { OtsStaffOrderStatus } from '../../core/types';
import { StaffOrderColumnComponent } from '../../shared/staff-order-column/staff-order-column.component';
import { StaffStatusFilterComponent } from '../../shared/staff-status-filter/staff-status-filter.component';

@Component({
  selector: 'ots-staff-dashboard-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, StaffOrderColumnComponent, StaffStatusFilterComponent],
  templateUrl: './staff-dashboard-page.component.html',
  styleUrl: './staff-dashboard-page.component.scss',
  host: {
    class: 'ots-staff-app d-flex flex-direction-column full-width full-height overflow-hidden c-bg-gray-100 c-t-blue-900 fw-regular'
  }
})
export class StaffDashboardPageComponent extends BasePageComponent {

  @Input() public showCloseButton = false;

  public readonly closeRequested = output<void>();

  private readonly _languageStore = inject(LanguageStore);
  private readonly _otsStaffOrdersStore = inject(OtsStaffOrdersStore);
  private readonly _otsStaffOrdersService = inject(OtsStaffOrdersService);
  private readonly _otsStaffDashboardPreferencesService = inject(OtsStaffDashboardPreferencesService);

  protected readonly searchQuery = this._otsStaffOrdersStore.searchQuery;
  protected readonly statusFilters = this._otsStaffOrdersStore.statusFilters;

  protected readonly cinemaId = computed((): number | null => {
    const rawCinemaId = this.terminalsStore.selectedTerminal()?.cinemaId;
    const parsedCinemaId = rawCinemaId == null ? null : Number(rawCinemaId);

    return parsedCinemaId != null && Number.isFinite(parsedCinemaId) && parsedCinemaId > 0
      ? parsedCinemaId
      : null;
  });

  private readonly _cinemaId$ = toObservable(this.cinemaId);

  protected readonly loadError = this._otsStaffOrdersStore.loadError;

  private readonly orders = this._otsStaffOrdersStore.orders;

  private readonly _now = toSignal(
    interval(60000).pipe(
      startWith(0),
      map(() => Date.now())
    ),
    { initialValue: Date.now() }
  );

  private readonly cards = computed(() => {
    this._languageStore.language();
    this._now();
    const query = this.searchQuery().trim().toLowerCase();
    const movingOrderIds = this._otsStaffOrdersStore.movingOrderIds();
    const orderCards = this.orders().map(order => OtsStaffOrderCardUtils.format(order, movingOrderIds.has(order.id)));

    if (!query) {
      return orderCards;
    }

    return orderCards.filter(card => this.matchesQuery(card, query));
  });

  protected readonly totalLabel = computed(() => {
    this._languageStore.language();
    const count = this.orders().length;

    return count === 1
      ? '[[count]] order total'.trans('ots.staff.total.one', [['count', count]])
      : '[[count]] orders total'.trans('ots.staff.total', [['count', count]]);
  });

  protected readonly newCards = computed(() => this.cardsByStatus('new'));
  protected readonly preparingCards = computed(() => this.cardsByStatus('preparing'));
  protected readonly readyCards = computed(() => this.cardsByStatus('ready'));
  protected readonly doneCards = computed(() => this.cardsByStatus('done'));

  protected readonly newTitle = computed(() => this.columnTitle('NEW', 'ots.staff.column.new', this.newCards().length));
  protected readonly preparingTitle = computed(() =>
    this.columnTitle('PREPARING', 'ots.staff.column.preparing', this.preparingCards().length)
  );
  protected readonly readyTitle = computed(() =>
    this.columnTitle('READY', 'ots.staff.column.ready', this.readyCards().length)
  );
  protected readonly doneTitle = computed(() => this.columnTitle('DONE', 'ots.staff.column.done', this.doneCards().length));

  protected readonly statusFilterOptions = computed(() => {
    this._languageStore.language();
    const filters = this.statusFilters();

    return [
      { id: 'new' as const, label: 'New'.trans('ots.staff.status.new'), checked: filters.new },
      {
        id: 'preparing' as const,
        label: 'Preparing'.trans('ots.staff.status.preparing'),
        checked: filters.preparing
      },
      {
        id: 'ready' as const,
        label: 'Ready'.trans('ots.staff.status.ready'),
        checked: filters.ready
      },
      { id: 'done' as const, label: 'Done'.trans('ots.staff.status.done'), checked: filters.done }
    ];
  });

  protected readonly statusFiltersActive = computed(() =>
    Object.values(this.statusFilters()).some(isColumnVisible => !isColumnVisible)
  );

  public ngOnInit(): void {
    this._otsStaffDashboardPreferencesService.restoreStatusFilters();

    this._otsStaffOrdersService.watchCinema(this._cinemaId$, this.destroyRef);
  }

  protected onSearchInput(event: Event): void {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    this._otsStaffOrdersService.setSearchQuery(target.value);
  }

  protected toggleStatus(status: OtsStaffOrderStatus): void {
    this._otsStaffDashboardPreferencesService.toggleStatus(status);
  }

  protected moveToNextStatus(orderId: string): void {
    this._otsStaffOrdersService.moveToNextStatus(orderId);
  }

  private cardsByStatus(status: OtsStaffOrderStatus): IOtsStaffOrderCard[] {
    return this.cards().filter(card => card.status === status);
  }

  private columnTitle(fallback: string, key: string, count: number): string {
    this._languageStore.language();
    return '[[label]] ([[count]])'.trans('ots.staff.column', [
      ['label', fallback.trans(key)],
      ['count', count]
    ]);
  }

  private matchesQuery(card: IOtsStaffOrderCard, query: string): boolean {
    const haystack = [
      card.id,
      card.numberLabel,
      card.metaLine,
      card.locationLine,
      card.fulfillmentLabel,
      ...card.lines.map(line => line.label)
    ].join(' ').toLowerCase();

    return haystack.includes(query);
  }

  public ngOnDestroy(): void {
    this._otsStaffOrdersService.setSearchQuery('');
  }
}
